// Apolo IDE — GitContextHeader (ui, sprint composer-anchor E2).
//
// A âncora "você está aqui" do composer: owner/repo · branch · dir + cor de
// saúde, dentro do card (sem glass próprio — não empilha glass sobre glass).
// Mostra o alvo AUTORITATIVO (git vivo do dir resolvido pelo core), nunca a
// pref crua. Sinais de risco passivos: branch protegida = cadeado âmbar; modo
// Direto = badge âmbar. Pixel-fiel ao mockup aprovado.

import { useState } from 'react';
import { AlertOctagon, AlertTriangle, Check, CircleCheck, GitBranch, Lock } from 'lucide-react';

// (Pedro) — colors stripped: the git context reads as a calm, minimal light
// line. State is carried by the icon + label, not by color.
const HEALTH = {
  synced: { dot: 'bg-apolo-textTertiary', text: 'text-apolo-textTertiary', Icon: CircleCheck, label: 'sincronizado' },
  unconfirmed: { dot: 'bg-apolo-textTertiary', text: 'text-apolo-textTertiary', Icon: AlertTriangle, label: 'não confirmado' },
  conflict: { dot: 'bg-apolo-textTertiary', text: 'text-apolo-textTertiary', Icon: AlertOctagon, label: 'conflito' },
};

function abbreviateDir(dir) {
  if (!dir || typeof dir !== 'string') return null;
  return dir.replace(/^\/(?:Users|home)\/[^/]+\//, '~/');
}

/**
 * @param {{ context: import('../../lib/gitContext.js').GitContext, mode?: 'worktree'|'direct', className?: string, inline?: boolean }} props
 *   inline=true → drops `pb-2.5 border-b` chrome so the header pode viver
 *   embutido numa única row (Pedro 2026-06-25, sprint cursor-strip-2).
 */
// Cor do # da PR por estado: aberta=verde, mergeada=roxo, fechada=vermelho.
const PR_STATE_COLOR = {
  open: 'text-apolo-success',
  merged: 'text-violet-400',
  closed: 'text-apolo-error',
};

export default function GitContextHeader({ context, mode = 'worktree', className = '', inline = false, onSlugClick = null, onBranchClick = null, onDirClick = null, prNumber = null, prUrl = null, prState = null }) {
  const [copied, setCopied] = useState(null);
  if (!context) return null;
  const { slug, branch, dir, health, reason, protectedBranch } = context;
  const h = HEALTH[health] || HEALTH.synced;
  const shownDir = abbreviateDir(dir);
  const direct = mode === 'direct';

  // The anchor used to be dead static text. Each part is now a real button that
  // copies its value (repo / branch / full path) with a transient ✓ — actionable
  // without coupling to the parent's git actions.
  async function copy(value, key) {
    if (!value || typeof navigator === 'undefined' || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1200);
    } catch { /* clipboard blocked — no-op */ }
  }

  return (
    <div
      role="status"
      aria-label={`Alvo da sessão: ${slug || 'sem alvo'}${branch ? `, branch ${branch}` : ''}. ${reason}`}
      className={
        // (Pedro 2026-06-25, sprint cursor-strip-2) — inline drop pb/border-b
        // pra header viver embutido na MESMA row do diff stat / actions.
        'flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] ' +
        (inline ? '' : 'pb-2.5 border-b border-apolo-borderSubtle ') +
        className
      }
    >
      {/* (Pedro 2026-06-25, sprint cursor-strip-4 round 5) — quando o branch
         atual tem uma PR aberta, o # dela SUBSTITUI a bolinha de status à
         esquerda do slug. Clicável → abre a PR no GitHub. Sem PR, mostra a
         bolinha de saúde original. */}
      {prNumber != null ? (
        prUrl ? (
          <a
            href={prUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid="git-header-pr"
            title={`Abrir PR #${prNumber}${prState ? ` (${prState})` : ''} no GitHub`}
            className={`shrink-0 font-mono text-[12px] font-semibold transition hover:underline ${PR_STATE_COLOR[prState] || 'text-apolo-textSecondary'}`}
          >
            #{prNumber}
          </a>
        ) : (
          <span
            data-testid="git-header-pr"
            className={`shrink-0 font-mono text-[12px] font-semibold ${PR_STATE_COLOR[prState] || 'text-apolo-textSecondary'}`}
          >
            #{prNumber}
          </span>
        )
      ) : (
        <span className={`h-2 w-2 shrink-0 rounded-full ${h.dot}`} aria-hidden="true" />
      )}

      {slug ? (
        <button
          type="button"
          onClick={(e) => {
            // (Pedro 2026-06-25, sprint cursor-strip-3) — quando o pai passa
            // onSlugClick (ex: GitConnectionBar pra abrir o repo picker), o
            // click vira "trocar de pasta" em vez de "copiar slug". Sem o
            // handler, mantém o copy-to-clipboard original (outros consumers).
            if (typeof onSlugClick === 'function') {
              e.preventDefault();
              onSlugClick(e);
              return;
            }
            copy(slug, 'slug');
          }}
          title={typeof onSlugClick === 'function' ? 'Trocar de pasta / abrir repo' : 'Clique para copiar o repositório'}
          data-testid="git-slug-button"
          className="inline-flex max-w-full items-center gap-1 truncate rounded text-[14px] font-medium text-apolo-textPrimary transition hover:text-apolo-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
        >
          <span className="truncate">{slug}</span>
          {copied === 'slug' && <Check size={12} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />}
        </button>
      ) : (
        <span className="text-[13px] text-apolo-textTertiary">Sem alvo configurado</span>
      )}

      {branch && (
        <button
          type="button"
          onClick={(e) => {
            // (Pedro 2026-06-25, sprint cursor-strip-3) — quando o pai passa
            // onBranchClick (ex: GitConnectionBar pra abrir o branch switcher),
            // o click vira "trocar de branch". Sem handler, copia o nome.
            if (typeof onBranchClick === 'function') {
              e.preventDefault();
              onBranchClick(e);
              return;
            }
            copy(branch, 'branch');
          }}
          data-testid="git-branch-button"
          className={
            'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 leading-none transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing ' +
            (protectedBranch
              ? 'border-apolo-borderStrong text-apolo-textSecondary hover:text-apolo-textPrimary'
              : 'border-apolo-borderSubtle text-apolo-textSecondary hover:border-apolo-borderStrong hover:text-apolo-textPrimary')
          }
          title={
            typeof onBranchClick === 'function'
              ? (protectedBranch ? 'Branch protegida — clique para trocar de branch' : 'Trocar de branch')
              : (protectedBranch ? 'Branch protegida — clique para copiar o nome' : 'Clique para copiar a branch')
          }
        >
          {copied === 'branch' ? (
            <Check size={11} aria-hidden="true" className="text-apolo-textTertiary" />
          ) : protectedBranch ? (
            <Lock size={11} aria-hidden="true" />
          ) : (
            <GitBranch size={11} aria-hidden="true" />
          )}
          {branch}
          {protectedBranch && <span className="sr-only"> (branch protegida)</span>}
        </button>
      )}

      {shownDir && (
        <button
          type="button"
          onClick={(e) => {
            // Quando o pai passa onDirClick (composer home → trocar de pasta),
            // o click no caminho abre o seletor; sem handler, copia o caminho.
            if (typeof onDirClick === 'function') {
              e.preventDefault();
              onDirClick(e);
              return;
            }
            copy(dir, 'dir');
          }}
          data-testid="git-dir-button"
          title={typeof onDirClick === 'function' ? `Trocar de pasta (${dir})` : `Clique para copiar o caminho: ${dir}`}
          className="hidden max-w-full items-center gap-1 truncate rounded font-mono text-[11px] text-apolo-textTertiary transition hover:text-apolo-textSecondary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing sm:inline-flex"
        >
          <span className="truncate">{shownDir}</span>
          {copied === 'dir' && <Check size={11} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />}
        </button>
      )}

      <span className={`${inline ? '' : 'ml-auto '}inline-flex shrink-0 items-center gap-2`}>
        {direct && (
          <>
            <span
              className="inline-flex items-center gap-1 text-[11px] leading-none text-apolo-textTertiary"
              title="Modo Direto: escreve na árvore de trabalho viva (sem isolamento de worktree)"
            >
              <AlertTriangle size={11} aria-hidden="true" />
              Direto
            </span>
            <span aria-hidden="true" className="text-apolo-textTertiary">·</span>
          </>
        )}
        <span className={`inline-flex items-center gap-1 leading-none ${h.text}`}>
          <h.Icon size={13} aria-hidden="true" />
          {h.label}
        </span>
      </span>
    </div>
  );
}
